/**
 * ClientActionsSection — PM-facing UI for managing client action items.
 *
 * Rendered inside ProjectDetailPage (gated by portal_management feature flag).
 * PMs can create, edit position/due-date, and delete action items.
 */
import { useState } from 'react';
import { useOperationalSettings } from '@/hooks/useSettings';
import { Plus, Trash2, GripVertical, CheckCircle2, Clock, ThumbsUp, AlertTriangle, Hourglass, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useClientActions,
  useCreateClientAction,
  useUpdateClientAction,
  useDeleteClientAction,
} from '@/hooks/useClientActions';

const DEFAULT_OVERDUE_HOURS = 48;

interface ClientActionsSectionProps {
  projectId: string;
}

const ACTION_TYPES = [
  { value: 'info', label: 'Information' },
  { value: 'review', label: 'Review & Approve' },
  { value: 'upload', label: 'Upload Document' },
  { value: 'payment', label: 'Payment' },
  { value: 'approval', label: 'Approval' },
];

const STATUS_BADGE: Record<string, { label: string; classes: string }> = {
  pending:    { label: 'Pending',   classes: 'bg-gray-100 text-gray-700' },
  completed:  { label: 'Done',      classes: 'bg-green-100 text-green-700' },
  in_progress:{ label: 'In Progress', classes: 'bg-yellow-100 text-yellow-700' },
  skipped:    { label: 'Skipped',   classes: 'bg-gray-100 text-gray-500' },
};

/** Return acceptance status: 'accepted' | 'overdue' | 'pending' | null (if no tracking) */
function getAcceptanceStatus(action: any, overdueHours: number): 'accepted' | 'overdue' | 'pending' | null {
  // V118 adds accepted_at — undefined means old data before migration
  if (action.accepted_at === undefined) return null;
  if (action.accepted_at) return 'accepted';
  // Overdue = created > overdueHours ago and not yet accepted
  if (action.created_at) {
    const ageHours = (Date.now() - new Date(action.created_at).getTime()) / 3_600_000;
    if (ageHours > overdueHours) return 'overdue';
  }
  return 'pending';
}

function formatDate(d: string) {
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
}

function formatRelativeOrShort(d: string): string {
  try {
    const date = new Date(d);
    const diffMs = Date.now() - date.getTime();
    const diffHours = diffMs / 3_600_000;
    if (diffHours < 1) return 'just now';
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    if (diffHours < 48) return 'yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
}

interface AddFormState {
  title: string;
  action_type: string;
  due_date: string;
  description: string;
}

const EMPTY_FORM: AddFormState = {
  title: '',
  action_type: 'info',
  due_date: '',
  description: '',
};

export function ClientActionsSection({ projectId }: ClientActionsSectionProps) {
  const { data: operationalSettings } = useOperationalSettings();
  const overdueHours = operationalSettings?.acceptance_reminder_hours ?? DEFAULT_OVERDUE_HOURS;
  const { data, isLoading } = useClientActions(projectId);
  const createAction = useCreateClientAction();
  const updateAction = useUpdateClientAction();
  const deleteAction = useDeleteClientAction();

  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState<AddFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const actions = data?.actions ?? [];

  const handleAdd = async () => {
    if (!form.title.trim()) {
      setFormError('Title is required');
      return;
    }
    setFormError(null);
    try {
      await createAction.mutateAsync({
        projectId,
        data: {
          title: form.title.trim(),
          action_type: form.action_type,
          description: form.description.trim() || undefined,
          due_date: form.due_date || undefined,
          position: actions.length,
        },
      });
      setForm(EMPTY_FORM);
      setShowAddForm(false);
    } catch {
      setFormError('Failed to add action item. Please try again.');
    }
  };

  const handleDelete = (actionId: string) => {
    deleteAction.mutate({ projectId, actionId });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const action = actions[index];
    updateAction.mutate({
      projectId,
      actionId: action.id,
      data: { position: index - 1 },
    });
    const above = actions[index - 1];
    updateAction.mutate({
      projectId,
      actionId: above.id,
      data: { position: index },
    });
  };

  const handleMoveDown = (index: number) => {
    if (index >= actions.length - 1) return;
    const action = actions[index];
    updateAction.mutate({
      projectId,
      actionId: action.id,
      data: { position: index + 1 },
    });
    const below = actions[index + 1];
    updateAction.mutate({
      projectId,
      actionId: below.id,
      data: { position: index },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // Compute acceptance summary (only for actions that have the accepted_at field — V118+)
  const trackableActions = actions.filter(a => a.accepted_at !== undefined);
  const acceptedCount = trackableActions.filter(a => a.accepted_at).length;
  const overdueAcceptance = trackableActions.filter(a => {
    if (a.accepted_at) return false;
    if (!a.created_at) return false;
    return (Date.now() - new Date(a.created_at).getTime()) / 3_600_000 > overdueHours;
  }).length;
  const pendingAcceptance = trackableActions.filter(a => !a.accepted_at).length;

  return (
    <div className="space-y-3">
      {actions.length === 0 && !showAddForm && (
        <p className="text-sm text-muted-foreground py-2">
          No client action items yet. Add items for your client to complete via the portal.
        </p>
      )}

      {/* Acceptance summary — shown when there are trackable items */}
      {trackableActions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
          <span className="font-medium text-gray-700">Acceptance:</span>
          <span className="inline-flex items-center gap-1">
            <ThumbsUp className="h-3 w-3 text-green-600" />
            <span className="text-green-700">{acceptedCount} accepted</span>
          </span>
          {pendingAcceptance > 0 && (
            <span className="inline-flex items-center gap-1">
              <Hourglass className="h-3 w-3 text-yellow-600" />
              <span className={overdueAcceptance > 0 ? 'text-red-700 font-medium' : 'text-yellow-700'}>
                {pendingAcceptance} pending{overdueAcceptance > 0 ? ` (${overdueAcceptance} overdue)` : ''}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Action list */}
      {actions.length > 0 && (
        <ul className="space-y-2">
          {actions.map((action, idx) => {
            const statusInfo = STATUS_BADGE[action.status] ?? STATUS_BADGE.pending;
            const overdue = action.is_overdue ?? false;

            const acceptStatus = getAcceptanceStatus(action, overdueHours);

            return (
              <li
                key={action.id}
                className="flex items-start gap-2 rounded-lg border bg-card p-3"
                data-testid={`pm-action-${action.id}`}
              >
                {/* Reorder controls */}
                <div className="flex flex-col gap-0.5 mt-0.5">
                  <button
                    type="button"
                    onClick={() => handleMoveUp(idx)}
                    disabled={idx === 0}
                    className="text-gray-300 hover:text-gray-500 disabled:opacity-30 text-xs leading-none"
                    aria-label="Move up"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveDown(idx)}
                    disabled={idx === actions.length - 1}
                    className="text-gray-300 hover:text-gray-500 disabled:opacity-30 text-xs leading-none"
                    aria-label="Move down"
                  >
                    ▼
                  </button>
                </div>

                {/* Status icon */}
                <div className="mt-0.5 flex-shrink-0">
                  {action.status === 'completed'
                    ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                    : <Clock className="h-4 w-4 text-gray-400" />
                  }
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">{action.title}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${statusInfo.classes}`}>
                      {statusInfo.label}
                    </span>
                    {overdue && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                        Overdue
                      </span>
                    )}
                    {/* Acceptance status badge (Phase 10e — only shown after V118 migration) */}
                    {acceptStatus === 'accepted' && (
                      <span
                        className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700"
                        title={`Accepted by ${action.accepted_by_email ?? 'portal user'}${action.accepted_at ? ` on ${formatDate(action.accepted_at)}` : ''}`}
                      >
                        <ThumbsUp className="h-3 w-3" />
                        Accepted {action.accepted_at ? formatRelativeOrShort(action.accepted_at) : ''}
                      </span>
                    )}
                    {acceptStatus === 'overdue' && (
                      <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700" title="Not accepted — over 48 hours">
                        <AlertTriangle className="h-3 w-3" />
                        Acceptance Overdue
                      </span>
                    )}
                    {acceptStatus === 'pending' && (
                      <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700" title="Awaiting acceptance from portal user">
                        <Hourglass className="h-3 w-3" />
                        Pending Acceptance
                      </span>
                    )}
                    {/* F8: surface the link to the document review when the action
                     *  is wired to one — admin sees "Document Review" badge and
                     *  can click through to the review viewer. */}
                    {action.document_review_id && (
                      <a
                        href={`/document-reviews`}
                        onClick={(e) => {
                          // Avoid stealing focus when accidental click — only
                          // navigate when the user clicks the visible link.
                          e.stopPropagation();
                        }}
                        className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100"
                        title="This action is wired to a document review"
                        data-testid={`action-review-link-${action.id}`}
                      >
                        <FileText className="h-3 w-3" />
                        Document Review
                      </a>
                    )}
                  </div>
                  {action.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{action.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    <span className="capitalize">{action.action_type}</span>
                    {action.due_date && (
                      <span className={overdue ? 'text-red-600' : ''}>
                        Due {formatDate(action.due_date)}
                      </span>
                    )}
                    {action.completed_by_email && (
                      <span>By {action.completed_by_email}</span>
                    )}
                  </div>
                </div>

                {/* Delete */}
                <button
                  type="button"
                  onClick={() => handleDelete(action.id)}
                  disabled={deleteAction.isPending}
                  aria-label={`Delete "${action.title}"`}
                  className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors mt-0.5"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Add form */}
      {showAddForm && (
        <div className="rounded-lg border p-4 space-y-3 bg-muted/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 space-y-1">
              <Label htmlFor="ca-title">Title *</Label>
              <Input
                id="ca-title"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Sign the service agreement"
                autoFocus
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="ca-type">Type</Label>
              <select
                id="ca-type"
                value={form.action_type}
                onChange={e => setForm(f => ({ ...f, action_type: e.target.value }))}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                {ACTION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="ca-due">Due Date</Label>
              <Input
                id="ca-due"
                type="date"
                value={form.due_date}
                onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
              />
            </div>

            <div className="sm:col-span-2 space-y-1">
              <Label htmlFor="ca-desc">Description (optional)</Label>
              <Input
                id="ca-desc"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Additional instructions for the client"
              />
            </div>
          </div>

          {formError && (
            <p className="text-sm text-red-600">{formError}</p>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowAddForm(false); setForm(EMPTY_FORM); setFormError(null); }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={createAction.isPending}
            >
              {createAction.isPending ? 'Adding…' : 'Add Item'}
            </Button>
          </div>
        </div>
      )}

      {/* Add button */}
      {!showAddForm && (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setShowAddForm(true)}
        >
          <Plus className="h-4 w-4" />
          Add Action Item
        </Button>
      )}
    </div>
  );
}
